"use client";

import * as React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from "recharts";
import { format, parseISO, isValid, subDays } from "date-fns";
import api from "../api/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Alert, AlertDescription } from "./ui/alert";
import { AlertCircle, Users, TrendingUp, Calendar, RefreshCw, UserCheck, Shield } from "lucide-react";
import { Badge } from "./ui/badge";

export default function RecruiterAnalyticsChart() {
  const [range, setRange] = React.useState("1m");
  const [page, setPage] = React.useState(1);
  const [summary, setSummary] = React.useState(null);
  const [daily, setDaily] = React.useState([]);
  const [recent, setRecent] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const getRangeLabel = (value) => {
    const btn = rangeButtons.find(b => b.value === value);
    return btn ? btn.label : value;
  };

  const fetchData = React.useCallback(async (r, p = 1) => {
    setLoading(true);
    setError(null);
    setIsRefreshing(true);
    try {
      
      const res = await api.get(
        `/admin/recruiter-analytics?range=${r}&page=${p}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );

      
      if (res.data) {
        setSummary(res.data.summary);
        
        // Handle daily data - ensure proper format
        const formattedDaily = Array.isArray(res.data.daily) 
          ? res.data.daily.map(d => ({
              date: d.date || d.Date || format(new Date(), 'yyyy-MM-dd'),
              count: Number(d.count) || 0,
              name: format(parseISO(d.date || d.Date || new Date().toISOString()), "MMM d")
            }))
          : [];
        
        setDaily(formattedDaily);
        
        setRecent(res.data.recentRecruiters || []);
        setPage(res.data.page || 1);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch recruiter analytics:", err);
      setError(err.response?.data?.message || err.message || "Failed to load data");
      
      // Fallback mock data for debugging
      if (process.env.NODE_ENV === 'development') {
        setSummary({
          total: 125,
          week: 8,
          month: 32,
          threeMonths: 85,
          year: 120
        });
        setDaily(generateMockDailyData(range));
        setRecent([
          {
            id: "1",
            email: "recruiter1@example.com",
            companyName: "Tech Corp Inc",
            location: "San Francisco, CA",
            createdAt: new Date().toISOString(),
            isVerified: true
          },
          {
            id: "2",
            email: "recruiter2@example.com",
            companyName: "Marketing Pro",
            location: "New York, NY",
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            isVerified: false
          }
        ]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData(range, 1);
  }, [range]); // Only re-fetch when range changes, not page

  const rangeButtons = [
    { label: "Last 7 days", value: "7d", icon: Calendar },
    { label: "Last month", value: "1m", icon: TrendingUp },
    { label: "Last 3 months", value: "3m", icon: Users },
    { label: "Last year", value: "1y", icon: UserCheck },
    { label: "All time", value: "all", icon: Shield },
  ];

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchData(range, newPage);
    }
  };

  const handleRangeChange = (newRange) => {
    setRange(newRange);
    setPage(1); // Reset to first page when range changes
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "Invalid date";
      return format(date, "MMM d, yyyy");
    } catch (err) {
      return "Invalid date";
    }
  };

  // Generate mock data for debugging
  const generateMockDailyData = (range) => {
    const days = range === "7d" ? 7 : range === "1m" ? 30 : range === "3m" ? 90 : range === "1y" ? 365 : 30;
    
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      return {
        date: format(date, 'yyyy-MM-dd'),
        count: Math.floor(Math.random() * 5) + 1,
        name: format(date, "MMM d")
      };
    });
  };

  return (
    <Card className="w-full border shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                Recruiter Registration Analytics
              </CardTitle>
              <CardDescription className="mt-1 text-sm">
                Monitoring recruiter growth for {getRangeLabel(range)}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(range, page)}
              disabled={isRefreshing}
              className="gap-2 w-full sm:w-auto"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        
          <div className="flex flex-wrap gap-2">
            {rangeButtons.map((b) => {
              const Icon = b.icon;
              return (
                <Button
                  key={b.value}
                  size="sm"
                  variant={range === b.value ? "default" : "outline"}
                  onClick={() => handleRangeChange(b.value)}
                  className="gap-1.5 text-xs sm:text-sm flex-1 sm:flex-none"
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{b.label}</span>
                  <span className="sm:hidden">{b.label.replace('Last ', '').replace(' days', 'd').replace(' month', 'm').replace(' months', 'm').replace(' year', 'y').replace('All time', 'All')}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}


        {/* Chart Section */}
{/* Chart Section */}
<div className="border rounded-lg p-4">
  <h3 className="font-semibold mb-4 text-lg">Recruiter Registration Trend</h3>
  {loading ? (
    <div className="space-y-2">
      <Skeleton className="h-64 w-full rounded" />
      <Skeleton className="h-4 w-3/4 rounded" />
      <Skeleton className="h-4 w-1/2 rounded" />
    </div>
  ) : daily.length === 0 ? (
    <div className="text-center py-12 border rounded bg-muted/30">
      <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
      <p className="text-muted-foreground">No recruiter registrations in this period.</p>
      <p className="text-sm text-muted-foreground mt-1">
        Try selecting a different time range
      </p>
    </div>
  ) : (
    <>
      {/* Debug info above chart */}
      {daily.length === 1 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-yellow-800 text-sm">
            <span className="font-semibold">Note:</span> Only one data point available. 
            This could mean all recruiters registered on the same day, or there's an issue with data aggregation.
          </div>
        </div>
      )}
      
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={daily}
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(value, index) => {
              // Show all labels if few points, otherwise space them out
              if (daily.length <= 15) return value;
              if (daily.length > 30 && index % 7 !== 0) return '';
              return value;
            }}
          />
          <YAxis 
            stroke="#6b7280" 
            fontSize={12}
            allowDecimals={false}
            domain={[0, 'dataMax + 1']} // Ensure y-axis starts at 0
            label={{ 
              value: 'Recruiters', 
              angle: -90, 
              position: 'insideLeft',
              offset: -10,
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip
            contentStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return formatDate(payload[0].payload.date);
              }
              return label;
            }}
            formatter={(value, name, props) => {
              const date = props.payload.date;
              const count = value;
              return [
                <div key="tooltip">
                  <div className="font-bold">{count} recruiter{count !== 1 ? 's' : ''}</div>
                  <div className="text-xs text-gray-500">on {formatDate(date)}</div>
                </div>,
                'Registrations'
              ];
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="count"
            name="Daily Registrations"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ r: 4, strokeWidth: 2 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
            isAnimationActive={!isRefreshing}
            connectNulls={true}
          />
          
          {/* Add a reference line at 0 for better visibility */}
          {daily.length === 1 && (
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
          )}
        </LineChart>
      </ResponsiveContainer>
      
      {/* Alternative visualization for single data point */}
      {daily.length === 1 && (
        <div className="mt-6">
          <div className="text-center mb-2">
            <div className="text-4xl font-bold text-blue-600">
              {daily[0].count}
            </div>
            <div className="text-sm text-gray-500">
              recruiter{daily[0].count !== 1 ? 's' : ''} registered on {formatDate(daily[0].date)}
            </div>
          </div>
        </div>
      )}
    </>
  )}
</div>
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {[
              { 
                label: "Total Recruiters", 
                value: summary.total, 
                icon: Users,
                color: "bg-blue-500/10 text-blue-700 dark:text-blue-300"
              },
              { 
                label: "Last 7 Days", 
                value: summary.week, 
                icon: TrendingUp,
                color: "bg-green-500/10 text-green-700 dark:text-green-300"
              },
              { 
                label: "Last Month", 
                value: summary.month, 
                icon: Calendar,
                color: "bg-purple-500/10 text-purple-700 dark:text-purple-300"
              },
              { 
                label: "Last 3 Months", 
                value: summary.threeMonths, 
                icon: Users,
                color: "bg-orange-500/10 text-orange-700 dark:text-orange-300"
              },
              { 
                label: "Last Year", 
                value: summary.year, 
                icon: TrendingUp,
                color: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300"
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className={`p-3 sm:p-4 rounded-lg border ${s.color} transition-transform hover:scale-[1.02]`}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 opacity-70" />
                    <Badge variant="secondary" className="text-xs hidden sm:inline-flex">
                      {s.label.includes('7') ? 'Weekly' : 
                       s.label.includes('Month') ? 'Monthly' : 
                       s.label.includes('Year') ? 'Yearly' : 'Total'}
                    </Badge>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold">{s.value}</div>
                  <div className="text-xs opacity-70 mt-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Recruiters Table */}
        <div className="border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
            <h3 className="font-semibold text-base sm:text-lg">Recent Recruiters (10 per page)</h3>
            <Badge variant="secondary" className="w-fit">
              Page {page} of {totalPages}
            </Badge>
          </div>
          
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 border rounded bg-muted/30">
              <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No recent recruiters found.</p>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="block md:hidden space-y-3">
                {recent.map((recruiter) => (
                  <Card key={recruiter.id || recruiter.email} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm break-all">{recruiter.email}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {recruiter.companyName || recruiter.recruiterProfile?.companyName || "N/A"}
                          </div>
                        </div>
                        <Badge variant={recruiter.isVerified ? "default" : "outline"} className="text-xs shrink-0">
                          {recruiter.isVerified ? "✓" : "⏳"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="truncate">{recruiter.location || recruiter.recruiterProfile?.location || "—"}</span>
                        <span className="shrink-0 ml-2">{formatDate(recruiter.createdAt)}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Joined Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((recruiter) => (
                      <TableRow key={recruiter.id || recruiter.email} className="hover:bg-muted/50">
                        <TableCell className="font-medium">
                          {recruiter.email}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {recruiter.companyName || recruiter.recruiterProfile?.companyName || "N/A"}
                          </div>
                          {recruiter.website && (
                            <div className="text-xs text-muted-foreground">
                              <a 
                                href={recruiter.website.startsWith('http') ? recruiter.website : `https://${recruiter.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline text-primary"
                              >
                                {recruiter.website.replace(/^https?:\/\//, '')}
                              </a>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {recruiter.location || recruiter.recruiterProfile?.location || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={recruiter.isVerified ? "default" : "outline"}>
                            {recruiter.isVerified ? "✓ Verified" : "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatDate(recruiter.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                  <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                    Showing {recent.length} recruiter(s) on page {page} of {totalPages}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || loading}
                      className="w-full sm:w-auto text-xs sm:text-sm"
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1 overflow-x-auto max-w-full px-2">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className="w-8 h-8 sm:w-9 sm:h-9 p-0 text-xs sm:text-sm"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages || loading}
                      className="w-full sm:w-auto text-xs sm:text-sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}